import { ActionItemStatus } from "@/store/models/clientUpdates/actionItem";
import { AuthModule } from "@/store/modules/auth.module";
import { hasRight } from "@/store/helper";
import { RightsConstant } from "@/store/securityRightConstant";
// An update with no Interaction Type
export const NOTES_LABEL = "Notes";
// Fixed List Manager GUIDs (mirror System1Constants on the backend)
export const UPDATE_SIDE_CLIENT_ID = "8c11a8e4-00ec-4b4e-bb48-3709fd27819b";
export const UPDATE_SIDE_DEVELOPMENT_ID = "087db21f-e6fc-4c2e-b47d-41c5eefbcd8d";
// Closed items are reopened on the Action Items page only
export const CLOSED_ACTION_ITEM_HINT = "This action item is closed. Reopen it on the Action Items page to change it.";
// Longest action item text - matches PMOActionItems.Description VARCHAR(200) and ActionItemValidator
export const ACTION_ITEM_MAX_LENGTH = 200;
export const sameId = (a, b) => {
    if (a == null || b == null) {
        return false;
    }
    return a.toString().toLowerCase() === b.toString().toLowerCase();
};
// Only the owner (the user who created it) or the assignee may edit or delete an action item - same rule
// as the API. The login's `sub` is the Employee id stored in both fields.
export const canModifyActionItem = (item) => {
    const userId = AuthModule.userIdValue;
    return sameId(item.createdBy, userId) || sameId(item.assigneeUserId, userId);
};
export const NOT_OWNER_OR_ASSIGNEE_HINT = "Only the owner or the assignee can change this action item.";
// Status changes - complete, reopen and close - are also open to anyone with the PMO Clients right (same rule as the API)
export const canChangeActionItemStatus = (item) => {
    return canModifyActionItem(item) || hasRight(RightsConstant.Clients.BasicPrivilege());
};
export const NOT_ALLOWED_TO_CHANGE_STATUS_HINT = "Only the owner, the assignee or a user with Clients access can change the status of this action item.";
// Same text as the API's error for a deactivated client
export const DEACTIVATED_CLIENT_HINT = "This client is deactivated. Its updates and action items are read-only.";
// Add Update goes to the Team filter's team, so one must be picked first
export const SELECT_TEAM_HINT = "Select a team to add an update.";
// A deactivated team picked in the Team filter - its updates can be viewed, but no new ones added
export const DEACTIVATED_TEAM_HINT = "This team is deactivated. Its updates can be viewed but not added.";
// A new action item goes to the Team filter's team (when one is picked), and a deactivated team takes no new ones
export const DEACTIVATED_TEAM_ACTION_ITEM_HINT = "This team is deactivated. Action items can't be added to it.";
// Deactivated client's item - the API refuses any change. Missing flag (older payloads) counts as active.
export const isClientReadOnlyItem = (item) => item.isClientActive === false;
// Two action items are the same task when the text (trimmed, ignoring case) and the assignee match
export const actionItemKey = (description, assigneeUserId) => `${(description || "").trim().toLowerCase()}|${(assigneeUserId || "").toLowerCase()}`;
// "yyyy-MM-dd" strings are parsed as a local calendar date (new Date() would treat them as UTC).
export const parseDate = (value) => {
    if (value == null || value === "") {
        return null;
    }
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
    return isNaN(date.getTime()) ? null : date;
};
// The search term as a case-insensitive pattern, taken literally. A space in it matches any run of spaces,
// the editor's non-breaking spaces included, but never a line break - so a hit stays inside one line.
const searchPattern = (term, flags) => {
    const needle = (term || "").trim();
    if (!needle) {
        return null;
    }
    const source = needle
        .split(/\s+/)
        .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("[^\\S\\n]+");
    return new RegExp(source, flags);
};
// True when the search term appears in the text (same matching as the highlight)
export const textMatches = (value, term) => {
    const pattern = searchPattern(term, "i");
    return !!pattern && !!value && pattern.test(value);
};
// Splits text into matched/unmatched parts for search highlighting
export const highlightParts = (value, term) => {
    const text = value || "";
    const pattern = searchPattern(term, "gi");
    if (!text || !pattern) {
        return [{ text: text, hit: false }];
    }
    const parts = [];
    let position = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > position) {
            parts.push({ text: text.slice(position, match.index), hit: false });
        }
        parts.push({ text: match[0], hit: true });
        position = match.index + match[0].length;
    }
    if (position < text.length) {
        parts.push({ text: text.slice(position), hit: false });
    }
    return parts;
};
// True for any day after today (date pickers pass local-midnight dates)
export const isAfterToday = (date) => date > startOfToday();
// True for any day before today
export const isBeforeToday = (date) => date < startOfToday();
// Calendar day of a server date ("2026-03-01T00:00:00") or picker value, as local midnight - the time part is ignored
export const toCalendarDay = (value) => {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return parseDate(value.substring(0, 10));
    }
    const date = parseDate(value);
    return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
};
// True when the day falls before the (optional) minimum day
export const isBeforeDay = (date, minDay) => {
    const day = toCalendarDay(date);
    return !!day && !!minDay && day < minDay;
};
// True when the search term appears in an action item's text or assignee name
export const actionItemMatches = (item, term) => {
    return [item.description, item.assigneeName].some((value) => textMatches(value, term));
};
// DD MMM YYYY, en-GB - the only date format used in this feature
export const formatDate = (value) => {
    const date = parseDate(value);
    if (!date) {
        return "";
    }
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
// Value for <input type="date">
export const toInputDate = (value) => {
    const date = parseDate(value);
    if (!date) {
        return "";
    }
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    return `${date.getFullYear()}-${month}-${day}`;
};
export const monthsAgo = (months) => {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    return date;
};
export const daysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
};
export const startOfToday = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};
// "Overdue" is derived for display only: Open and due before today.
export const getActionItemDisplayStatus = (item) => {
    if (item.status === ActionItemStatus.Completed) {
        return "Completed";
    }
    if (item.status === ActionItemStatus.Closed) {
        return "Closed";
    }
    const due = parseDate(item.dueDate);
    if (due && due < startOfToday()) {
        return "Overdue";
    }
    return "Open";
};
const createdOnTime = (value) => {
    if (value == null || value === "") {
        return 0;
    }
    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return isNaN(time) ? 0 : time;
};
// Newest action item first on an update (not due-date order)
export const byNewestActionItem = (a, b) => {
    return createdOnTime(b.createdOn) - createdOnTime(a.createdOn);
};
// Only http(s) URLs become clickable - anything else renders as plain text
export const safeUrl = (url) => {
    return url && /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
};
export const getInitials = (name) => {
    if (!name) {
        return "";
    }
    const parts = name.trim().split(/\s+/);
    const first = parts[0] ? parts[0].charAt(0) : "";
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
    return (first + last).toUpperCase();
};
// Plain text of editor HTML: one line per paragraph, list item or line break
export const htmlToText = (html) => {
    if (!html) {
        return "";
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    const blocks = doc.body.querySelectorAll("p, li, br");
    blocks.forEach((node) => {
        if (node.nodeName === "BR") {
            node.replaceWith("\n");
        }
        else {
            node.appendChild(doc.createTextNode("\n"));
        }
    });
    return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};
// An update's text (its HTML is the only copy), cached per HTML - search runs it on every entry per keystroke
const updateTextCache = new Map();
export const updateText = (html) => {
    if (!html) {
        return "";
    }
    let text = updateTextCache.get(html);
    if (text === undefined) {
        text = htmlToText(html);
        if (updateTextCache.size >= 1000) {
            updateTextCache.clear();
        }
        updateTextCache.set(html, text);
    }
    return text;
};
// Tags the Updates editor toolbar can produce. Anything else is unwrapped so its text remains.
const UPDATE_HTML_TAGS = ["P", "BR", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI"];
const UPDATE_HTML_DROP = ["SCRIPT", "STYLE", "NOSCRIPT", "IFRAME", "OBJECT", "EMBED"];
const appendCleanUpdateHtml = (source, target, doc) => {
    source.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            target.appendChild(doc.createTextNode(node.textContent || ""));
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        const element = node;
        if (UPDATE_HTML_DROP.indexOf(element.tagName) > -1) {
            return;
        }
        if (element.tagName === "BR") {
            target.appendChild(doc.createElement("br"));
            return;
        }
        if (UPDATE_HTML_TAGS.indexOf(element.tagName) > -1) {
            const copy = doc.createElement(element.tagName.toLowerCase());
            appendCleanUpdateHtml(element, copy, doc);
            target.appendChild(copy);
            return;
        }
        appendCleanUpdateHtml(element, target, doc);
    });
};
// Elements that start a new line in htmlToText - a hit never runs across them
const LINE_BREAK_TAGS = ["P", "LI", "BR", "UL", "OL"];
// Marks every hit in the element's text, also one that runs across formatting ("<b>urgent</b> fix")
const highlightElementText = (root, term) => {
    const pattern = searchPattern(term, "gi");
    if (!pattern) {
        return;
    }
    // All the text in one string, each text node's start offset noted; new lines as "\n", like htmlToText
    const nodes = [];
    let text = "";
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    let current = walker.nextNode();
    while (current) {
        if (current.nodeType === Node.TEXT_NODE) {
            nodes.push({ node: current, start: text.length });
            text += current.textContent || "";
        }
        else if (LINE_BREAK_TAGS.indexOf(current.tagName) > -1) {
            text += "\n";
        }
        current = walker.nextNode();
    }
    const hits = [];
    let match;
    while ((match = pattern.exec(text)) !== null) {
        hits.push({ from: match.index, to: match.index + match[0].length });
    }
    if (!hits.length) {
        return;
    }
    // Each text node gets the part of every hit that falls inside it
    nodes.forEach(({ node: textNode, start }) => {
        const value = textNode.textContent || "";
        const end = start + value.length;
        const parts = [];
        let position = 0;
        hits.filter((hit) => hit.from < end && hit.to > start).forEach((hit) => {
            const hitStart = Math.max(hit.from, start) - start;
            const hitEnd = Math.min(hit.to, end) - start;
            if (hitStart > position) {
                parts.push({ text: value.slice(position, hitStart), hit: false });
            }
            parts.push({ text: value.slice(hitStart, hitEnd), hit: true });
            position = hitEnd;
        });
        if (!parts.length) {
            return;
        }
        if (position < value.length) {
            parts.push({ text: value.slice(position), hit: false });
        }
        const fragment = document.createDocumentFragment();
        parts.forEach((part) => {
            if (part.hit) {
                const mark = document.createElement("span");
                mark.className = "up-hl";
                mark.textContent = part.text;
                fragment.appendChild(mark);
            }
            else {
                fragment.appendChild(document.createTextNode(part.text));
            }
        });
        if (textNode.parentNode) {
            textNode.parentNode.replaceChild(fragment, textNode);
        }
    });
};
// Safe HTML for timeline display: formatting from the editor, search hits marked, no attributes.
export const formatUpdateHtml = (html, term) => {
    if (!html) {
        return "";
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    const clean = doc.createElement("div");
    appendCleanUpdateHtml(doc.body, clean, doc);
    const needle = (term || "").trim();
    if (needle) {
        highlightElementText(clean, needle);
    }
    return clean.innerHTML;
};
